/* eslint-disable class-methods-use-this */
/* eslint-disable perfectionist/sort-classes */
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Location } from 'react-router';

import { NOTIF_TYPES } from '@private/notifications';
import { i18n, TFunction } from 'i18next';

import { STATUSES } from 'constants/common';
import { queryClient } from 'queries';
import { withRouter } from 'routes/ErrorBoundary/withRouter';
import AccessForbidden from 'routes/pages/UnauthorizedStack/SomethingWrongPages/AccessForbidden';
import NotFound from 'routes/pages/UnauthorizedStack/SomethingWrongPages/NotFound';
import PermissionDenied from 'routes/pages/UnauthorizedStack/SomethingWrongPages/PermissionDenied';
import SomethingWrong from 'routes/pages/UnauthorizedStack/SomethingWrongPages/SomethingWrong';
import { throttle } from 'utils/common';
import { notificationService } from 'utils/notificationService';
import { captureException } from 'utils/sentryLazy';
import { QueryError } from 'utils/types';

interface IHandleRequestErrorProps {
  callback?: () => void;
  error: QueryError;
  i18n: i18n;
  t: TFunction;
}

interface Props extends WithTranslation {
  children: React.ReactNode;
  router?: {
    location: Location;
  };
}

interface State {
  error: unknown;
  hasError: boolean;
}

const hasResponseProperty = (error: unknown): error is { response: unknown } => {
  return typeof error === 'object' && error !== null && 'response' in error;
};

const throttledCaptureException = throttle((error) => {
  captureException(error);
}, 1000);

export const handleRequestError = ({
  callback,
  error,
  i18n,
  t,
}: IHandleRequestErrorProps) => {
  if (error?.code === 'ECONNABORTED') {
    callback?.();

    let message = t('notifications.requestFailed');

    if (i18n.exists(error?.message)) {
      message = t(error?.message as never);
    } else {
      message = error?.message;
    }

    notificationService.show({
      dismissIn: false,
      message: error?.response?.data?.message || message,
      title: t('notifications.errorTitle'),
      toastId: error?.response?.data?.message || message,
      type: NOTIF_TYPES.ERROR,
    });
  }
};

class ErrorBoundary extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const state: State = {
      error: null,
      hasError: false,
    };

    this.state = state;
  }

  public static getDerivedStateFromError(error: Error): State {
    return { error, hasError: true };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (
      prevState.hasError ||
      /*
        Reset the error state when navigating away before an error occurs.
        This ensures that errors from previous pages do not trigger on the new page.
      */
      // eslint-disable-next-line react/destructuring-assignment
      prevProps.router?.location !== this.props.router?.location
    ) {
      this.resetError();
    }
  }

  componentDidCatch(error: unknown, _errorInfo: React.ErrorInfo) {
    throttledCaptureException(error);

    const { i18n, t } = this.props;
    handleRequestError({
      callback: this.resetError,
      error: error as QueryError,
      i18n,
      t,
    });
  }

  componentWillUnmount() {
    this.resetError();
  }

  resetError = () => {
    const { hasError } = this.state;
    if (hasError) {
      this.setState({
        error: null,
        hasError: false,
      });
      queryClient.resetQueries({
        predicate: (query) => {
          return query.state.status !== 'success';
        },
      });
    }
  };

  isPermissionError(error: unknown): boolean {
    return (
      hasResponseProperty(error) &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).response?.data?.exception?.includes('AuthorizationException')
    );
  }

  isAccessDeniedError(error: unknown): boolean {
    return (
      hasResponseProperty(error) &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).response?.status === STATUSES.AUTHORIZATION
    );
  }

  isNotFoundError(error: unknown): boolean {
    return (
      hasResponseProperty(error) &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).response?.status === STATUSES.NOT_FOUND
    );
  }

  render() {
    const { children, i18n, t } = this.props;
    const { error, hasError } = this.state;

    if (this.isPermissionError(error)) {
      return <PermissionDenied onReset={this.resetError} />;
    }

    const message = (error as QueryError)?.response?.data?.message;
    const subtitleMessage =
      message && i18n.exists(`error.messages.${message}`)
        ? t(`error.messages.${message}` as never)
        : null;

    if (this.isAccessDeniedError(error)) {
      return <AccessForbidden onReset={this.resetError} subtitle={subtitleMessage} />;
    }

    if (this.isNotFoundError(error)) {
      return <NotFound />;
    }

    if (hasError) {
      return <SomethingWrong onReset={this.resetError} />;
    }

    return children;
  }
}

export default withTranslation()(withRouter(ErrorBoundary));
